// Upsell-list adds skip the heavy drawer render inside the add request; the
// drawer is painted once afterwards, when the gifts are settled (see
// nobl_finishFastAdd in assets/cart.js). Set to false to restore the old behaviour.
const NOBL_UPSELL_FAST_ADD = false;

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Trigger loading bar start
        this.triggerLoadingStart();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const fastAdd = NOBL_UPSELL_FAST_ADD &&
          !!this.cart && this.cart.tagName === 'CART-DRAWER' &&
          !!this.closest('.upsell_product') && !this.closest('quick-add-modal') &&
          typeof window.nobl_finishFastAdd === 'function';
        if (this.cart) {
          formData.append(
            'sections',
            fastAdd
              ? 'cart-icon-bubble'
              : this.cart.getSectionsToRender().map((section) =>
                  section.id === 'cart-drawer' && this.cart.tagName === 'CART-DRAWER' ? 'cart-drawer-add' : section.id
                )
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // FIX (pre-checkout gift sync): register this add-to-cart as "in flight"
        // so GiftManager's Checkout sync can wait for it (and for the
        // cartUpdate subscribers) instead of checking the cart before the new
        // product has landed. Also invalidates the cached cart.
        window.NOBLCartSync = window.NOBLCartSync || {};
        const nsync = window.NOBLCartSync;
        if (!nsync._adds) nsync._adds = new Set();
        if (!nsync.waitForAdds) {
          nsync.waitForAdds = async function () {
            while (nsync._adds.size > 0) await Promise.allSettled(Array.from(nsync._adds));
          };
        }
        nsync.writeSeq = (nsync.writeSeq || 0) + 1;
        nsync.lastCart = null;

        // FIX (lost-line race): Shopify drops one of two cart writes that overlap.
        // If a free-gift /cart/update.js is in flight, let it finish before this
        // add is sent (max 6 s). GiftManager in turn skips starting a gift write
        // while an add is registered in nsync._adds.
        const gm = window.GiftManager;
        const giftWriteDone = gm && gm.updating
          ? (async () => {
              const t0 = Date.now();
              while (gm.updating && Date.now() - t0 < 6000) await new Promise((r) => setTimeout(r, 30));
            })()
          : null;
        const addPromise = (giftWriteDone || Promise.resolve())
          .then(() => fetch(`${routes.cart_add_url}`, config))
          .then((response) => response.json())
          .then(async (response) => {
            // The add request asks for the 'cart-drawer-add' section (same markup as
            // 'cart-drawer', cheaper to render). Expose it under the usual key.
            if (!response.status && response.sections) {
              if (response.sections['cart-drawer-add']) {
                response.sections['cart-drawer'] = response.sections['cart-drawer-add'];
              } else if (!fastAdd && !response.sections['cart-drawer'] && this.cart && this.cart.tagName === 'CART-DRAWER') {
                try {
                  const r = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
                  if (r.ok) response.sections['cart-drawer'] = await r.text();
                } catch (e) { console.error(e); }
              }
            }
            return response;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            if (window.NOBLCrossSellColorMatch) {
              window.NOBLCrossSellColorMatch.setLastAddedColor(
                window.NOBLCrossSellColorMatch.extractColorFromLineItem(response)
              );
            }
            if (fastAdd && !this.error) {
              // The full drawer is painted later by GiftManager (or by the
              // safety timer below), after the gifts have settled.
              nsync.fastAddPending = true;
              nsync.fastAddSeq = nsync.writeSeq;
              setTimeout(() => {
                if (nsync.fastAddPending) window.nobl_finishFastAdd(null);
              }, 12000);
            }
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
                fastAdd: fastAdd,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (fastAdd) {
              // Same as CartDrawer.renderContents, minus the drawer markup
              // (not requested): update the bubble and open the drawer.
              const cartEl = this.cart;
              const inner = cartEl.querySelector('.drawer__inner');
              if (inner) inner.classList.remove('is-empty');
              const bubble = document.getElementById('cart-icon-bubble');
              if (bubble && response.sections && response.sections['cart-icon-bubble']) {
                bubble.innerHTML = cartEl.getSectionInnerHTML(response.sections['cart-icon-bubble']);
              }
              setTimeout(() => cartEl.open());
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            CartPerformance.measureFromEvent("add:user-action", evt);
            
            // Trigger loading bar end
            this.triggerLoadingEnd();
          });
        nsync._adds.add(addPromise);
        addPromise.finally(() => nsync._adds.delete(addPromise));
      }

      // Helper methods to trigger loading bar events
      triggerLoadingStart() {
        const loadingStartEvent = new CustomEvent('theme:loading:start', {
          bubbles: true,
          cancelable: true
        });
        document.dispatchEvent(loadingStartEvent);
      }

      triggerLoadingEnd() {
        const loadingEndEvent = new CustomEvent('theme:loading:end', {
          bubbles: true,
          cancelable: true
        });
        document.dispatchEvent(loadingEndEvent);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = false, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}